import TopBar from '../components/common/TopBar';
import VendorChangeTickets from '../components/vendors/VendorChangeTickets';
import VendorList from '../components/vendors/VendorList';
import { useSetTitle } from '../components/hooks/useSetTitle';
import useSetOrg from '../components/hooks/useSetOrg';
import useGlynt from '../components/hooks/useGlynt';
import DialogGlynt from '../components/common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../components/common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../components/common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../components/common/Dialog/DialogActionsGlynt';
import TableStyles from '../components/documents/Table.module.css';
import { Tabs, Tab, Box, Alert, Button, IconButton, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import CloseIcon from '@mui/icons-material/Close';
import React, { useContext, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppContext } from '../App';
import { IDataPool } from '../types/my-types';

function VendorsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { dataPoolId } = useContext(AppContext);

  // Restore org state on refresh — dataPoolId survives in sessionStorage
  // but org (useState) is lost without useSetOrg
  useSetOrg(dataPoolId);

  const glynt = useGlynt();
  const queryClient = useQueryClient();
  const { data: dataPool } = useQuery({
    queryKey: ['dataPool', dataPoolId],
    queryFn: async () => (await glynt).dataPool.retrieve(),
    enabled: !!dataPoolId,
    refetchOnWindowFocus: false
  });
  const suggestVendorsDisabled = dataPoolId && dataPool && dataPool.config?.suggest_new_vendors !== true;

  const [alertDismissed, setAlertDismissed] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const enableMutation = useMutation({
    mutationFn: async () => {
      return (await glynt).dataPool.update(dataPoolId!, {
        config: { ...dataPool?.config, suggest_new_vendors: true },
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['dataPool', dataPoolId]
      });
      queryClient.invalidateQueries({
        queryKey: ['datapools']
      });
      setConfirmOpen(false);
      setAlertDismissed(true);
    }
  });

  const getTabIndexFromSearch = (search: string): number => {
    const params = new URLSearchParams(search);
    const tabParam = params.get('tab');
    if (tabParam === 'vendor-list') return 1;

    return 0;
  };

  const [tab, setTab] = useState<number>(() => getTabIndexFromSearch(location.search));

  // Sync state when user navigates via back/forward or external link
  React.useEffect(() => {
    const idx = getTabIndexFromSearch(location.search);
    if (idx !== tab) setTab(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const updateUrlForTab = (nextTab: number) => {
    const params = new URLSearchParams(location.search);
    params.set('tab', nextTab === 1 ? 'vendor-list' : 'change-tickets');
    navigate({ pathname: location.pathname, search: params.toString() });
  };

  const title = tab === 0 ? 'Vendor Change Tickets' : 'Vendors';
  useSetTitle(title);

  return (
    <div id="training-page">
      <TopBar />
      <div className={TableStyles.documentsPageWrapper}>
        {suggestVendorsDisabled && !alertDismissed && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            data-cy="alert-suggest-vendors-disabled"
            action={
              <>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setConfirmOpen(true)}
                  data-cy="btn-enable-suggest-vendors"
                >
                  Enable
                </Button>
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={() => setAlertDismissed(true)}
                  data-cy="btn-dismiss-suggest-vendors-alert"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            }
          >
            This data pool does not have suggest new vendors enabled.
          </Alert>
        )}
        <DialogGlynt
          open={confirmOpen}
          closeModal={() => setConfirmOpen(false)}
          loading={enableMutation.isPending}
          cypressId="dialog-enable-suggest-vendors"
        >
          <DialogTitleGlynt
            title="Enable Suggest New Vendors"
            closeModal={() => setConfirmOpen(false)}
            loading={enableMutation.isPending}
          />
          <DialogContentGlynt>
            <Typography>
              Are you sure you want to enable suggest new vendors for this data pool?
            </Typography>
          </DialogContentGlynt>
          <DialogActionsGlynt
            handleSubmit={() => enableMutation.mutate()}
            closeModal={() => setConfirmOpen(false)}
            cancelCypressId="btn-enable-suggest-vendors-cancel"
            submitCypressId="btn-enable-suggest-vendors-confirm"
            clearCypressId=""
            submitButtonText="Enable"
            submitLoading={enableMutation.isPending}
            showClearButton={false}
          />
        </DialogGlynt>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '1.5rem' }}>
          <Tabs
            value={tab}
            onChange={(event: React.SyntheticEvent, value: number) => {
              setTab(value);
              updateUrlForTab(value);
            }}
            aria-label="Vendor page tabs"
            data-cy="vendors-page-tabs"
          >
            <Tab label="Change Tickets" data-cy="vendors-tab-change-tickets" />
            <Tab label="Vendor List" data-cy="vendors-tab-vendor-list" />
          </Tabs>
        </Box>
        {tab === 0 && (
          <Box data-cy="vendors-tabpanel-change-tickets">
            <VendorChangeTickets />
          </Box>
        )}
        {tab === 1 && (
          <Box data-cy="vendors-tabpanel-vendor-list">
            <VendorList />
          </Box>
        )}
      </div>
    </div>
  );
}
export default VendorsPage;
